export function getVolunteerFullName(volunteer: {
  firstName: string;
  lastName: string;
}): string {
  return `${volunteer.firstName} ${volunteer.lastName}`.trim();
}

/**
 * Send-flag defaults for a volunteer being CREATED. Spread alongside the rest
 * of the `data` in `prisma.volunteer.create`. Do not use on an update — see the
 * caveat at the bottom.
 *
 * The emails cron selects `isSubscribed: false, unsubscribeConfirmationSent:
 * false` and reads that as "just unsubscribed". But `isSubscribed` defaults to
 * false, so on a brand-new row it means "never subscribed" — someone who
 * claimed a need or offered help and left the mailing-list box unticked. They
 * are then mailed "You've been unsubscribed from NeedBridge" for a list they
 * were never on. 106 people in production received exactly that.
 *
 * Claiming the flag at creation says "there is nothing to confirm here", which
 * is the truth, and keeps them out of a queue that re-reads them every fifteen
 * minutes forever.
 *
 * On UPDATE the pairing is different — see resubscribeSendFlags.
 */
/**
 * A welcome email is only worth sending while signing up is still recent enough
 * to remember. Past this, "Welcome to NeedBridge!" reads as a mistake — which is
 * what it would have been for the 14 volunteers, oldest from Nov 2025, sitting
 * unsent in the queue when this was written.
 *
 * Volunteers older than this are drained from the queue rather than left in it:
 * the flag is claimed without sending, so they stop being re-read every fifteen
 * minutes forever.
 */
export const WELCOME_EMAIL_MAX_AGE_DAYS = 4;

/** The oldest `createdAt` still eligible for a welcome email. */
export function welcomeEmailCutoff(now: Date = new Date()): Date {
  return new Date(
    now.getTime() - WELCOME_EMAIL_MAX_AGE_DAYS * 24 * 60 * 60 * 1000,
  );
}

export function newVolunteerSendFlags(isSubscribed: boolean): {
  isSubscribed: boolean;
  unsubscribeConfirmationSent: boolean;
} {
  return {
    isSubscribed,
    unsubscribeConfirmationSent: !isSubscribed,
  };
}

/**
 * Send-flag pairing for a volunteer being UPDATED. Spread alongside the rest of
 * the `data` in `prisma.volunteer.update`.
 *
 * The invariant is: one unsubscribe confirmation per subscribe -> unsubscribe
 * transition. That needs the flag re-armed whenever someone becomes subscribed,
 * because it may still be set from a previous cycle — either an earlier
 * unsubscribe that was confirmed, or a creation while unsubscribed, which claims
 * the flag up front (see newVolunteerSendFlags). Without re-arming, a volunteer
 * who subscribes and later unsubscribes for real is silently owed nothing.
 *
 * Re-arming on every write of `isSubscribed: true` is safe and needs no read of
 * the current row: the flag has no effect while someone is subscribed, since the
 * cron's confirmation queue requires `isSubscribed: false`. So "subscribed"
 * always implies "armed", and there is no transition to detect.
 *
 * Setting `isSubscribed: false` deliberately leaves the flag alone. It is
 * already false for anyone who was armed, so their confirmation sends; and true
 * for anyone created unsubscribed, so they are correctly left out of a queue
 * confirming a list they were never on.
 */
export function resubscribeSendFlags(isSubscribed: boolean): {
  isSubscribed: boolean;
  unsubscribeConfirmationSent?: boolean;
} {
  return isSubscribed
    ? { isSubscribed: true, unsubscribeConfirmationSent: false }
    : { isSubscribed: false };
}
